import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, ChevronLeft, Shield, Check } from 'lucide-react';
import { toast } from 'sonner';
import eyvoLogo from '../assets/eyvo_logo.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Microsoft / Okta SVG icons
const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const OktaIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <circle cx="24" cy="24" r="22" fill="#007DC1"/>
    <circle cx="24" cy="24" r="10" fill="white"/>
  </svg>
);

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Step 1: tenant selection  Step 2: email/password
  // Persist tenant slug so users don't re-enter company code after logout
  const savedTenant = localStorage.getItem('eyvo_tenant_slug') || '';
  const [step, setStep] = useState(savedTenant ? 2 : 1);
  const [tenantSlug, setTenantSlug] = useState(savedTenant);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentVersion, setCurrentVersion] = useState('');
  const [deployedAt, setDeployedAt] = useState('');
  const [dbHost, setDbHost] = useState('');
  const [dbName, setDbName] = useState('');

  // Captcha state
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/captcha`);
      setCaptchaId(r.data.captcha_id);
      setCaptchaImage(r.data.image);
      setCaptchaAnswer('');
    } catch {}
    setCaptchaLoading(false);
  };
  const [dbStatus, setDbStatus] = useState(null); // null=loading, 'connected', 'disconnected'
  const [dbUrl, setDbUrl] = useState('');
  const [sqlStatus, setSqlStatus] = useState(null); // null=loading, 'connected', 'disconnected', 'unconfigured'
  const [sqlDbName, setSqlDbName] = useState('');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/system/version`)
      .then(r => {
        setCurrentVersion(r.data?.current?.version || r.data?.version || '');
        setDeployedAt(r.data?.current?.deployed_at || r.data?.deployed_at || '');
        setDbHost(r.data?.db_host || '');
        setDbName(r.data?.db_name || '');
        setDbUrl(r.data?.db_url || '');
      })
      .catch(() => {});
    const fetchHealth = () => {
      axios.get(`${BACKEND_URL}/api/health`, { timeout: 12000 })
        .then(r => {
          const d = r.data || {};
          // MongoDB
          const dbVal = d.database;
          if (dbVal === 'connected') setDbStatus('connected');
          else if (dbVal === 'disconnected') setDbStatus('disconnected');
          else setDbStatus(null);
          // SQL Server
          const sqlVal = d.sql_status;
          if (sqlVal === 'connected') setSqlStatus('connected');
          else if (sqlVal === 'disconnected') setSqlStatus('disconnected');
          else if (sqlVal === 'unconfigured') setSqlStatus('unconfigured');
          else setSqlStatus(null);
          setSqlDbName(d.sql_database || '');
        })
        .catch(() => {
          // Network / timeout / 5xx: we do NOT know whether the DBs are down —
          // we only know our own fetch failed. Show "Checking..." (null) rather
          // than falsely reporting both as offline. Next poll will resolve it.
          setDbStatus(prev => (prev === 'connected' || prev === 'disconnected') ? prev : null);
          setSqlStatus(prev => (prev === 'connected' || prev === 'disconnected' || prev === 'unconfigured') ? prev : null);
        });
    };
    fetchHealth();
    const healthInterval = setInterval(fetchHealth, 30000);
    // Auto-fill tenant from URL param or saved localStorage
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tenant') || localStorage.getItem('eyvo_tenant_slug');
    if (t) { setTenantSlug(t); handleTenantLookup(t); }
    return () => clearInterval(healthInterval);
  }, []); // eslint-disable-line

  const handleTenantLookup = async (slug) => {
    const s = (slug || tenantSlug).trim().toLowerCase();
    if (!s) { setTenantError('Please enter your company code.'); return; }
    setTenantLoading(true); setTenantError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/check-tenant`, {
        tenant_id: s
      });
      setTenantInfo(res.data);
      localStorage.setItem('eyvo_tenant_slug', s);
      setStep(2);
    } catch (err) {
      setTenantError(err.response?.data?.detail || 'Company not found. Check your company code.');
    }
    setTenantLoading(false);
  };

  const [sessionConflict, setSessionConflict] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regDone, setRegDone] = useState(false);

  const handleSubmit = async (e, force = false) => {
    e?.preventDefault();
    if (captchaRequired && captchaAnswer) {
      try {
        const cr = await axios.post(`${BACKEND_URL}/api/auth/captcha/verify`, { captcha_id: captchaId, answer: captchaAnswer });
        if (!cr.data.valid) {
          setError(cr.data.reason || 'Incorrect captcha. Please try again.');
          loadCaptcha();
          return;
        }
      } catch {
        setError('Captcha verification failed. Please try again.');
        loadCaptcha();
        return;
      }
    } else if (captchaRequired) {
      setError('Please enter the captcha code.');
      return;
    }
    setError('');
    setSessionConflict(false);
    setNeeds2FA(false);
    setLoading(true);
    try {
      const result = await login(email, password, tenantInfo?.tenant_id || tenantSlug, force);
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(result.temp_token);
        setLoading(false);
        return;
      }
      const returnTo = location.state?.from || '/dashboard';
      navigate(returnTo);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const data = err.response?.data || {};
      if (data.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(data.temp_token);
      } else if (detail === 'SESSION_CONFLICT') {
        setSessionConflict(true);
        setError('This account is already logged in from another session.');
      } else {
        setError(detail || 'Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/validate`, {
        temp_token: tempToken,
        code: totpCode,
      }, { withCredentials: true });  // H3 FIX: Use httpOnly cookie set by server
      if (res.data.access_token) {
        // H3 FIX: Removed localStorage.setItem - server sets httpOnly cookie
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSSOClick = (provider) => {
    toast.info(`${provider} SSO is configured in Admin → Environment Settings. Contact your system administrator to enable.`);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regForm.first_name || !regForm.last_name || !regForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setRegSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/self-register`, {
        ...regForm,
        tenant_id: tenantInfo?.tenant_id || tenantSlug,
      });
      setRegDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setRegSubmitting(false);
  };

  // Check if self-registration is allowed (via tenant settings)
  const selfRegEnabled = tenantInfo?.settings?.allow_self_registration;
  const captchaRequired = tenantInfo?.settings?.captcha_enabled;

  // Load captcha when login form shows and captcha is required
  useEffect(() => {
    if (step === 2 && captchaRequired && !captchaImage) loadCaptcha();
  }, [step, captchaRequired]); // eslint-disable-line

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'hsl(var(--background))' }}>
      {/* Left brand panel */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden"
        style={{ backgroundColor: 'hsl(var(--sidebar))' }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1589033291494-27dfeda9aefa?w=800&q=80')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          aria-hidden
        />
        <div className="relative z-10">
          {/* Eyvo logo */}
          <div className="mb-10">
            <img
              src={eyvoLogo}
              alt="Eyvo eProcurement"
              className="h-20 w-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Enterprise Procurement,<br />Simplified.
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-8">
            Secure purchasing, multi-level approvals, and supplier governance &mdash; all in one platform.
          </p>
          <div className="space-y-3">
            {[
              { icon: '🔒', text: 'SSO-ready — Okta & Microsoft Entra supported' },
              { icon: '📋', text: 'Full audit trails on every action' },
              { icon: '♿', text: 'WCAG 2.1 AA accessible' },
              { icon: '🌍', text: 'Multi-language & multi-currency' },
              { icon: '🔁', text: 'Bi-directional API for ERP & finance systems' },
              { icon: '🗣️', text: 'Foreign language support across the UI' },
              { icon: '🛡️', text: 'Role-based access control' },
              { icon: '✍️', text: 'Approvals routed by role and authority' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-base">{item.icon}</span>
                <span className="text-white/75 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center justify-between">
          <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} Eyvo eProcurement. All rights reserved.</p>
          <div className="flex items-center gap-3">
            {/* Database status indicators */}
            <a href="/eyvo-admin" className="flex items-center gap-3 no-underline" style={{ textDecoration: 'none' }}>
              {/* MDB indicator */}
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${dbStatus === 'connected' ? 'bg-green-400 animate-pulse' : dbStatus === 'disconnected' ? 'bg-red-400' : 'bg-yellow-400 animate-pulse'}`} />
                <span className="text-white/40 text-xs hover:text-white/60 transition-colors">
                  {dbStatus === 'connected' ? `MDB` : dbStatus === 'disconnected' ? 'MDB Offline' : 'Checking...'}
                </span>
              </div>
              {/* SQL indicator */}
              {sqlStatus && sqlStatus !== 'unconfigured' && (
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${sqlStatus === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                  <span className="text-white/40 text-xs hover:text-white/60 transition-colors">
                    {sqlStatus === 'connected' ? `SQL` : 'SQL Offline'}
                  </span>
                </div>
              )}
            </a>
            {currentVersion && (
              <span className="text-white/40 text-xs font-mono">v{currentVersion}</span>
            )}
            {deployedAt && (
              <span className="text-white/40 text-xs">
                Published {new Date(deployedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' })} {new Date(deployedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right login panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img src={eyvoLogo} alt="Eyvo eProcurement" className="h-10 w-auto" />
          </div>

          <Card className="shadow-lg border-border/60">
            <CardHeader className="pb-4">
              {step === 1 ? (
                <>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>{t('term.findOrganisation')}</CardTitle>
                  <CardDescription>Enter your company code to continue</CardDescription>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <button onClick={() => { setStep(1); setTenantInfo(null); setError(''); localStorage.removeItem('eyvo_tenant_slug'); }} className="text-muted-foreground hover:text-foreground">
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div>
                      <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>Sign In</CardTitle>
                      <CardDescription className="flex items-center gap-1.5 mt-0.5">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        {tenantInfo?.company_name}
                      </CardDescription>
                    </div>
                  </div>
                </>
              )}
            </CardHeader>
            <CardContent>
              {/* ── Step 1: Company Code ── */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="tenant-slug">Company Code</Label>
                    <Input
                      id="tenant-slug"
                      type="text"
                      value={tenantSlug}
                      onChange={e => setTenantSlug(e.target.value.toLowerCase().replace(/\s/g, '-'))}
                      placeholder="e.g. stark-industries"
                      onKeyDown={e => e.key === 'Enter' && handleTenantLookup()}
                      autoFocus
                      data-testid="tenant-slug-input"
                    />
                    <p className="text-xs text-muted-foreground">{t('term.companyCodeHint')}</p>
                  </div>
                  {tenantError && (
                    <div role="alert" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg border border-destructive/20">{tenantError}</div>
                  )}
                  <Button className="w-full" onClick={() => handleTenantLookup()} disabled={tenantLoading || !tenantSlug} data-testid="tenant-lookup-button">
                    {tenantLoading ? 'Looking up...' : 'Continue'}
                  </Button>
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                    <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">New to Eyvo?</span></div>
                  </div>
                  <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/onboard')}>
                    Start Free Trial
                  </Button>
                  {/* Database status + secret admin link */}
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <a href="/eyvo-admin" className="flex items-center gap-1.5 cursor-default hover:cursor-pointer group" style={{ textDecoration: 'none' }}>
                      <div className={`w-2 h-2 rounded-full ${dbStatus === 'connected' ? 'bg-green-500 animate-pulse' : dbStatus === 'disconnected' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'}`} />
                      <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        {dbStatus === 'connected' ? 'MDB' : dbStatus === 'disconnected' ? 'MDB Offline' : 'Checking...'}
                      </span>
                    </a>
                    {sqlStatus && sqlStatus !== 'unconfigured' && (
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${sqlStatus === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                        <span className="text-xs text-muted-foreground">
                          {sqlStatus === 'connected' ? 'SQL' : 'SQL Offline'}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-center text-[10px] text-muted-foreground/60 mt-2" data-testid="release-version">
                    {currentVersion ? `v${currentVersion}` : 'v2.1.0'}
                    {deployedAt && (
                      <>
                        {' '}&middot; Released {new Date(deployedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' })} {new Date(deployedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Los_Angeles' })} PT
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* ── Step 2: Authentication based on tenant config ── */}
              {step === 2 && (
                <>
                  {/* Show SSO buttons only if tenant uses SSO */}
                  {tenantInfo?.auth_method !== 'email_password' && (
                    <>
                      <div className="space-y-2 mb-5">
                        {(tenantInfo?.auth_method === 'microsoft_entra' || tenantInfo?.auth_method === 'entra_id') && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('Microsoft Entra')} data-testid="entra-sso-button">
                            <MicrosoftIcon /> Sign in with Microsoft Entra
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'okta_saml' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('Okta')} data-testid="okta-sso-button">
                            <OktaIcon /> Sign in with Okta
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'onelogin' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('OneLogin')} data-testid="onelogin-sso-button">
                            <Shield className="h-4 w-4" /> Sign in with OneLogin
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'auth0' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('Auth0')} data-testid="auth0-sso-button">
                            <Shield className="h-4 w-4" /> Sign in with Auth0
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'ping_identity' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('Ping Identity')} data-testid="ping-sso-button">
                            <Shield className="h-4 w-4" /> Sign in with Ping Identity
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'ldap' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('LDAP')} data-testid="ldap-sso-button">
                            <Shield className="h-4 w-4" /> Sign in with LDAP
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'jumpcloud' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('JumpCloud')} data-testid="jumpcloud-sso-button">
                            <Shield className="h-4 w-4" /> Sign in with JumpCloud
                          </Button>
                        )}
                        {tenantInfo?.auth_method === 'google_oauth' && (
                          <Button type="button" variant="outline" className="w-full flex items-center gap-3 h-10 font-medium" onClick={() => handleSSOClick('Google')} data-testid="google-sso-button">
                            <svg width="18" height="18" viewBox="0 0 48 48" className="mr-1"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                            Sign in with Google
                          </Button>
                        )}
                      </div>

                      {/* Supported SSO providers — always visible, indicator chips */}
                      <div className="mb-4">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground text-center mb-2 font-medium">
                          Single sign-on supported
                        </div>
                        <div className="flex items-center justify-center gap-3" data-testid="sso-providers-row">
                          <button
                            type="button"
                            onClick={() => handleSSOClick('Google')}
                            title="Sign in with Google"
                            aria-label="Sign in with Google"
                            data-testid="sso-icon-google"
                            className="group flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card hover:border-primary hover:shadow-md transition-all"
                          >
                            <svg width="22" height="22" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSSOClick('Microsoft Entra')}
                            title="Sign in with Microsoft Entra ID"
                            aria-label="Sign in with Microsoft Entra ID"
                            data-testid="sso-icon-entra"
                            className="group flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card hover:border-primary hover:shadow-md transition-all"
                          >
                            <svg width="22" height="22" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
                              <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                              <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                              <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                              <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSSOClick('Okta')}
                            title="Sign in with Okta"
                            aria-label="Sign in with Okta"
                            data-testid="sso-icon-okta"
                            className="group flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card hover:border-primary hover:shadow-md transition-all"
                          >
                            <svg width="22" height="22" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path fill="#007DC1" d="M16 6c-5.523 0-10 4.477-10 10s4.477 10 10 10 10-4.477 10-10S21.523 6 16 6zm0 14a4 4 0 110-8 4 4 0 010 8z"/>
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className="relative mb-5">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
                        <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">or sign in with email</span></div>
                      </div>
                    </>
                  )}
                  
                  {/* Self-Registration Form */}
                  {showRegister ? (
                    regDone ? (
                      <div className="text-center py-6">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="h-6 w-6 text-green-600" />
                        </div>
                        <p className="text-sm font-semibold">Request Submitted!</p>
                        <p className="text-xs text-muted-foreground mt-2">Your registration request has been sent to the administrators. You'll receive an email once your account is approved.</p>
                        <button className="text-xs text-primary hover:underline mt-4" onClick={() => { setShowRegister(false); setRegDone(false); setRegForm({ first_name: '', last_name: '', email: '', phone: '' }); }}>
                          &larr; Back to login
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleRegister} className="space-y-3">
                        <p className="text-sm font-semibold text-center mb-1">Request Access</p>
                        <p className="text-xs text-muted-foreground text-center mb-2">An administrator will review your request</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1"><Label className="text-xs">First Name *</Label><Input value={regForm.first_name} onChange={e => setRegForm(p => ({ ...p, first_name: e.target.value }))} required /></div>
                          <div className="space-y-1"><Label className="text-xs">Last Name *</Label><Input value={regForm.last_name} onChange={e => setRegForm(p => ({ ...p, last_name: e.target.value }))} required /></div>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">Email *</Label><Input type="email" value={regForm.email} onChange={e => setRegForm(p => ({ ...p, email: e.target.value }))} required /></div>
                        <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={regForm.phone} onChange={e => setRegForm(p => ({ ...p, phone: e.target.value }))} /></div>
                        <Button type="submit" className="w-full" disabled={regSubmitting}>{regSubmitting ? 'Submitting...' : 'Submit Request'}</Button>
                        <button type="button" className="w-full text-xs text-muted-foreground hover:text-primary" onClick={() => setShowRegister(false)}>&larr; Back to login</button>
                      </form>
                    )
                  ) : needs2FA ? (
                    <form onSubmit={handle2FAVerify} noValidate aria-label="Two-factor authentication">
                      <div className="space-y-4">
                        <div className="text-center py-2">
                          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                            <Shield className="h-6 w-6 text-primary" />
                          </div>
                          <p className="text-sm font-semibold">Two-Factor Authentication</p>
                          <p className="text-xs text-muted-foreground mt-1">Enter the 6-digit code from your authenticator app</p>
                        </div>
                        <Input
                          type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoFocus
                          value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000" className="text-center text-2xl tracking-[0.5em] font-mono"
                          data-testid="2fa-code-input"
                        />
                        {error && (
                          <div role="alert" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg border border-destructive/20">
                            <p>{error}</p>
                          </div>
                        )}
                        <Button type="submit" className="w-full" disabled={loading || totpCode.length !== 6} data-testid="2fa-verify-btn">
                          {loading ? 'Verifying...' : 'Verify Code'}
                        </Button>
                        <button type="button" className="w-full text-xs text-muted-foreground hover:text-primary" onClick={() => { setNeeds2FA(false); setTempToken(''); setTotpCode(''); setError(''); }}>
                          &larr; Back to login
                        </button>
                      </div>
                    </form>
                  ) : (
                  <form onSubmit={handleSubmit} noValidate aria-label="Login form">
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="email">Email or User ID</Label>
                        <Input id="email" type="text" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email or User ID" required aria-required data-testid="login-email-input" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">Password</Label>
                          <button type="button" className="text-xs text-primary hover:underline" aria-label="Forgot password">Forgot password?</button>
                        </div>
                        <div className="relative">
                          <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required aria-required data-testid="login-password-input" className="pr-10" />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      {error && (
                        <div role="alert" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg border border-destructive/20">
                          <p>{error}</p>
                          {sessionConflict && (
                            <Button type="button" size="sm" variant="destructive" className="mt-2 w-full" onClick={() => handleSubmit(null, true)} disabled={loading} data-testid="force-login-btn">
                              {loading ? 'Forcing...' : 'Force login — end other session'}
                            </Button>
                          )}
                        </div>
                      )}
                      {/* Captcha — when Secure Login is enabled */}
                      {captchaRequired ? (
                        <div className="space-y-1.5 pt-1">
                          <Label className="text-xs font-medium">Security Check</Label>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              {captchaImage ? (
                                <img src={captchaImage} alt="CAPTCHA" className="h-[46px] rounded-lg border" />
                              ) : (
                                <div className="h-[46px] w-[140px] bg-muted rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                                  {captchaLoading ? 'Loading...' : 'Click refresh'}
                                </div>
                              )}
                              <button type="button" onClick={loadCaptcha} className="text-xs text-primary hover:underline flex-shrink-0">
                                {captchaImage ? 'Refresh' : 'Load'}
                              </button>
                            </div>
                            <Input
                              type="text" maxLength={5} autoComplete="off"
                              value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value.toUpperCase())}
                              placeholder="Enter code above" className="font-mono tracking-wider uppercase"
                              data-testid="captcha-input"
                            />
                          </div>
                        </div>
                      ) : null}
                      <Button type="submit" className="w-full" disabled={loading || !email || !password || (captchaRequired && !captchaAnswer)} data-testid="login-submit-button">
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                      {/* Self-registration link */}
                      {selfRegEnabled ? (
                        <div className="text-center pt-2 border-t mt-3">
                          <p className="text-xs text-muted-foreground mb-1">Don't have an account?</p>
                          <button type="button" className="text-sm text-primary font-medium hover:underline" onClick={() => setShowRegister(true)} data-testid="self-register-link">
                            New user? Request access
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </form>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
